import {
    Inventory2Outlined as CabinetsIcon,
    PlaceOutlined as LocationsIcon,
    BusinessOutlined as OrganizationsIcon,
    AssessmentOutlined as StatisticsIcon,
    PersonOutlineOutlined as VisitorBookingIcon,
    LocalActivityOutlined as VouchersIcon,
} from '@mui/icons-material';
import { Building2, CircleHelp, ReceiptText, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { persistLanguage } from '../../i18n/i18n';
import { images } from '../../images/images';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Permission from '../../common/permissions';
import BookingsFill from '../../components/icons/BookingsFill';
import DashboardIcon from '../../components/icons/DashboardIcon';
import IssueReportFill from '../../components/icons/IssueReportFill';
import DepositsFill from '../../components/icons/DepositsFill';
import RentalProductsFill from '../../components/icons/RentalProductsFill';
import SmartLockerIcon from '../../components/icons/SmartLockerIcon';
import { paths } from '../../routes';
import {
    makeHasPermissionSelector,
    makePermissionListSelector,
    tenantSelector,
} from '../../selector/authenticationSelector';
import { RootState } from '../../store/store';
import { LogoutButton } from '../LogoutButton';
import { SidebarItem } from './SidebarItem';
import AssetsFill from '../../components/icons/AssetsFill';
import { canSeeCustomersArea } from '../../common/customersAreaAccess';
import { useSignOut } from '../../hooks/useSignOut';
import { useCanAccessAccount } from '../../hooks/useCanAccessAccount';
import { useIsInvoiceIssuingTenant } from '../../hooks/useIsInvoiceIssuingTenant';
import { roomUseCasesOf } from '../../utils/roomUseCases';
import { useIsMaster } from '../../common/useIsMaster';
import { isSelfServiceTenant } from '../../common/tenantStore';
import { useAccessManagementEntitlement } from '../../hooks/useCanUseAccessManagement';
import {
    accessManagementView,
    showAccessManagementEntry,
} from '../../utils/accessManagementGate';

interface SideBarContentProps {
    onClose: () => void;
}

export const SideBarContent = ({ onClose }: SideBarContentProps) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const signOut = useSignOut();
    const selectedTenant = useSelector(tenantSelector);
    const permissions = useSelector(makePermissionListSelector());
    const hasShowBookingsPermission = useSelector(
        makeHasPermissionSelector(Permission.showBookings()),
    );
    const hasShowDashboardPermission = useSelector(
        makeHasPermissionSelector(Permission.showDashboard()),
    );
    const hasShowLockersPermission = useSelector(
        makeHasPermissionSelector(Permission.showLockers()),
    );
    // Access management is the IoT add-on, and since ADR-0001 Amendment 5 a
    // tenant that operates no smart lock has nothing under it and therefore no
    // entry. Amendment 4 had shown the entry to every self-service tenant and
    // routed the unentitled ones to an upsell page; a venue tenant that hands
    // over keys by hand reads that as a broken menu item, not as an offer.
    //
    // The entry is derived from the ROUTE's decision (App.tsx computes the same
    // `accessManagementView`), so what the menu shows and what the page shows
    // cannot drift: the item is there exactly when there is something behind
    // it. `useGetAccountQuery` is already in flight for this screen via
    // `useCanAccessAccount` and in App, so this adds no request.
    //
    // Legacy tenants keep the permission gate: they resolve permissions from
    // the Auth0 JWT and carry no serialized entitlement, and they have real
    // lockers of their own, so `read:lockers` is the meaningful answer for
    // them. A master is no longer an exception: the entitlement is read for the
    // tenant IN VIEW (by slug), so a master switched to a venue tenant sees
    // that tenant's answer rather than their own `read:lockers` — except when
    // that read FAILS, where they fall back to it rather than lose a customer's
    // devices during an outage.
    const isMaster = useIsMaster();
    const {
        entitled: canUseAccessManagement,
        isResolving: accessManagementResolving,
        hasFailed: accessManagementLookupFailed,
    } = useAccessManagementEntitlement();
    const showAccessManagement = showAccessManagementEntry(accessManagementView(
        accessManagementResolving,
        hasShowLockersPermission,
        canUseAccessManagement,
        isSelfServiceTenant(selectedTenant),
        accessManagementLookupFailed,
        isMaster,
    ));
    const hasShowIssueReportsPermission = useSelector(
        makeHasPermissionSelector(Permission.showIssueReports()),
    );
    const hasAnyStatisticsPermission = permissions.some((permission) =>
        permission.startsWith(Permission.accessLocationsBookingStats()),
    );
    const hasVisitorBookingPermission = permissions.includes(
        Permission.editVisitorBooking(selectedTenant ?? ''),
    );
    const hasReadVouchersPermission = useSelector(
        makeHasPermissionSelector(Permission.readVouchers()),
    );
    // The invoice list (#3016): the endpoint's own string, and only for a
    // tenant kolula issues invoices for (ADR-0012 Mode A) — the same question
    // the booking screens ask, for the tenant in view (a master's too).
    const hasReadInvoicesPermission = useSelector(
        makeHasPermissionSelector(Permission.readInvoices()),
    );
    const isInvoiceIssuingTenant = useIsInvoiceIssuingTenant();
    // The Assets entry's own string (PO, 2026-10-07), not the asset-write one.
    const hasReadAssetsPermission = useSelector(
        makeHasPermissionSelector(Permission.readAssets()),
    );
    // Nav items gate on the caller's real permission set — DB-driven for
    // self-service (GET /admin/account/permissions, from `app_user.role`) and
    // JWT-driven for legacy. Previously a self-service tenant's JWT carried no
    // `tenant:<slug>` permissions (auth0 = authentication only, #740), so the
    // selectors resolved false and a self-service bypass surfaced
    // every item regardless of role. That bypass is removed (#1561) now that the
    // DB serves a differentiated set, so roles actually gate the sidebar. The
    // self-service check remains only for the self-service-only settings
    // section below (not a permission bypass). Backend still enforces tenant
    // scoping via `tenant_id` injection independent of what the sidebar shows.
    const selfServiceTenant = isSelfServiceTenant(selectedTenant);

    const config = useSelector(
        (state: RootState) => state.authentication.config,
    );
    const services = config?.services ?? [];
    const hasSupService = services.some((service) => service.toLowerCase() === 'sup');
    // **Services** — the renamed "Rental products" slot, now the home of the
    // per-use-case configuration (ADR-0005 amendment, 2026-08-18). The SUP-only
    // gate it carried is gone for self-service tenants: every one of them has
    // offers to configure there, SUP or not.
    //
    // It does NOT become unconditional. A legacy tenant has no operational
    // account record, so the configuration section cannot render for them and
    // the page holds only the SUP deposit list — which is exactly what the item
    // gated on before. Surfacing it to a legacy non-SUP tenant (dhu, vonovia,
    // segmenta) would put a permanently empty page in their menu.
    const canSeeServices = selfServiceTenant || hasSupService;

    // The account page is visible only to the tenant OWNER (full edit) and to a
    // MASTER viewing the tenant (read-only) — normal non-owner members don't see
    // it. Same guard backs the `/account` route. Edit/read-only and the
    // owner-only mutations are enforced in AccountPage + on the backend.
    const { canAccess: canSeeAccount } = useCanAccessAccount();

    // Bookings entry is always clickable so a brand-new tenant can land
    // on the empty bookings list and see what's possible. The
    // create-booking *button* on that page handles the "no assets yet"
    // case with its own messaging.

    const lockerItems = [
        {
            path: paths.dashboard,
            text: t('side_bar.dashboard'),
            icon: <DashboardIcon className={'h-5 w-5'} />,
            permission: hasShowDashboardPermission,
        },
        {
            path: selectedTenant === 'segmenta' ? paths.bookingSearch : paths.bookings,
            text: t('side_bar.bookings'),
            icon: <BookingsFill className={'h-5 w-5'} />,
            permission: hasShowBookingsPermission,
        },
        {
            path: paths.issueReports,
            text: t('side_bar.issue_reports'),
            icon: <IssueReportFill className={'h-5 w-5'} />,
            permission: hasShowIssueReportsPermission,
        },
        {
            path: paths.rentalProducts,
            text: t('side_bar.rental_products'),
            icon: <RentalProductsFill className={'h-5 w-5'} />,
            permission: canSeeServices,
        },
        {
            path: paths.deposits,
            text: t('side_bar.deposits'),
            icon: <DepositsFill className={'h-5 w-5'} />,
            permission: hasSupService,
        },
        {
            path: paths.depots,
            text: t('side_bar.access_management'),
            icon: <SmartLockerIcon className={'h-5 w-5'} />,
            permission: showAccessManagement,
        },
        {
            path: paths.locations,
            text: t('side_bar.locations'),
            icon: <LocationsIcon className={'h-5 w-5'} />,
            permission: false,
        },
        {
            path: paths.depots,
            text: t('side_bar.cabinets'),
            icon: <CabinetsIcon className={'h-5 w-5'} />,
            permission: false,
        },
        {
            path: paths.visitorBookings,
            text: t('side_bar.visitor_booking'),
            icon: <VisitorBookingIcon className={'h-5 w-5'} />,
            permission: hasVisitorBookingPermission,
        },
        {
            path: paths.statistics,
            text: t('side_bar.statistics'),
            icon: <StatisticsIcon className={'h-5 w-5'} />,
            permission: hasAnyStatisticsPermission,
        },
        {
            path: paths.assets,
            text: t('side_bar.assets'),
            icon: <AssetsFill className={'h-5 w-5'} />,
            // Assets overview (owner/manager, not service).
            // `demo` (legacy) keeps it via its tenant-specific gate.
            permission: hasReadAssetsPermission || selectedTenant === 'demo',
        },
        {
            // Customers — organizations + individual customers (H-3b).
            //
            // Was pinned to `demo` while the endpoints did not exist yet. They
            // do now (#2125), and they answer for every OPERATIONAL tenant:
            // `customerAccountController` / `organizationController` 409 when
            // the tenant has no `account` model, which is exactly the legacy
            // ones. So the gate is self-service, not one slug — `demo` was
            // never special here, it was just the first tenant to have a
            // backend to talk to.
            path: paths.customers,
            text: t('side_bar.customers'),
            icon: <OrganizationsIcon className={'h-5 w-5'} />,
            permission: canSeeCustomersArea(selectedTenant),
        },
    ];

    const rentalItems = [
        {
            path: paths.dashboard,
            text: t('side_bar.dashboard'),
            icon: <DashboardIcon className={'h-5 w-5'} />,
            permission: hasShowDashboardPermission,
        },
        {
            path: paths.rentalBooking,
            text: t('side_bar.bookings'),
            icon: <BookingsFill className={'h-5 w-5'} />,
            permission: hasShowBookingsPermission,
        },
        {
            path: paths.rentalIssueReports,
            text: t('side_bar.issue_reports'),
            icon: <IssueReportFill className={'h-5 w-5'} />,
            permission: hasShowIssueReportsPermission,
        },
        {
            path: paths.rentalProducts,
            text: t('side_bar.rental_products'),
            icon: <RentalProductsFill className={'h-5 w-5'} />,
            permission: canSeeServices,
        },
        {
            path: paths.deposits,
            text: t('side_bar.deposits'),
            icon: <DepositsFill className={'h-5 w-5'} />,
            permission: hasSupService,
        },
        {
            path: paths.assets,
            text: t('side_bar.assets'),
            icon: <AssetsFill className={'h-5 w-5'} />,
            // Assets overview — owner/manager, not service.
            permission: hasReadAssetsPermission,
        },
        {
            // Access management in the rental sidebar — same gate as the
            // station sidebar above: the tenant is entitled to the IoT add-on,
            // or is a legacy tenant / master holding `read:lockers`. A rental
            // tenant whose doors carry a static code manages that code on the
            // object, not here (ADR-0001 Amendment 3), and one with no devices
            // at all gets no entry (Amendment 5).
            path: paths.depots,
            text: t('side_bar.access_management'),
            icon: <SmartLockerIcon className={'h-5 w-5'} />,
            permission: showAccessManagement,
        },
        {
            // Customers — organizations + individual customers (H-3b).
            //
            // Was pinned to `demo` while the endpoints did not exist yet. They
            // do now (#2125), and they answer for every OPERATIONAL tenant:
            // `customerAccountController` / `organizationController` 409 when
            // the tenant has no `account` model, which is exactly the legacy
            // ones. So the gate is self-service, not one slug — `demo` was
            // never special here, it was just the first tenant to have a
            // backend to talk to.
            path: paths.customers,
            text: t('side_bar.customers'),
            icon: <OrganizationsIcon className={'h-5 w-5'} />,
            permission: canSeeCustomersArea(selectedTenant),
        },
        // {
        //     path: paths.rentalApartment,
        //     text: t('side_bar.apartments'),
        //     icon: <ApartmentFill className={'h-5 w-5'} />,
        // },
        // {
        //     path: paths.rentalAddOnServices,
        //     text: t('side_bar.Add_on_services'),
        //     icon: <AddOnServicesIcon className={'h-5 w-5'} />,
        // },
        // {
        //     path: paths.rentalPolices,
        //     text: t('side_bar.polices'),
        //     icon: <PolicesFill className={'h-5 w-5'} />,
        // },
    ];

    // Sidebar set is driven by the tenant's services rather than by
    // tenant type. Room-family services (the space template) get the
    // rental sidebar; its access-management entry appears only with the
    // add-on entitlement (code doors — the lock/locker hardware surface
    // stays a locker-sidebar concern). Everyone else — including the
    // legacy `demo` tenant — gets the locker sidebar.
    const useRentalSidebar = roomUseCasesOf(services).length > 0
        && selectedTenant !== 'demo';

    // Solution-agnostic nav — vouchers / promo codes / banners apply to any
    // paying tenant, so they render for BOTH the locker and rental sidebars
    // (not tied to lockers/station).
    const commonItems = [
        {
            path: paths.vouchersCodes,
            text: t('side_bar.vouchers_codes'),
            icon: <VouchersIcon className={'h-5 w-5'} />,
            // Gated on read:vouchers (owner/manager carry it; service does not).
            permission: hasReadVouchersPermission,
        },
        {
            // Issued invoices — any use case kolula invoices, so both sidebars.
            path: paths.invoices,
            text: t('side_bar.invoices'),
            icon: <ReceiptText className={'h-5 w-5'} />,
            permission: hasReadInvoicesPermission && isInvoiceIssuingTenant,
        },
    ];

    // Master Mode replaces the tenant-scoped sidebar entirely. When the
    // user is on any `/master/*` route, we surface the cross-tenant
    // entries instead of the regular per-tenant menu. The role check sits
    // on each page; this sidebar variant only renders the entries.
    const isMasterMode = location.pathname.startsWith('/master');

    // "Mandanten" first: Master mode opens on it (PO, 2026-11-02). Only a
    // master gets it — the page sends anyone else to /unauthorized, and a
    // service/manager user with the all-tenant view would otherwise find
    // that dead link at the top of the menu.
    const masterItems = [
        ...(isMaster ? [{
            path: paths.masterTenants,
            text: t('master.tenants.title'),
            icon: <Building2 className={'h-5 w-5'} />,
        }] : []),
        {
            path: paths.masterBookings,
            text: t('master.bookings'),
            icon: <BookingsFill className={'h-5 w-5'} />,
        },
        {
            path: paths.masterProblemReports,
            text: t('master.problem_reports'),
            icon: <IssueReportFill className={'h-5 w-5'} />,
        },
        {
            path: paths.masterLockers,
            text: t('master.lockers'),
            icon: <SmartLockerIcon className={'h-5 w-5'} />,
        },
    ];

    return (
        <div className={'h-full flex flex-col gap-3 overflow-y-auto'}>
            {isMasterMode && masterItems.map((item) => (
                <SidebarItem
                    className={'py-1'}
                    key={item.path}
                    to={item.path}
                    text={item.text}
                    icon={item.icon}
                    onClose={onClose}
                />
            ))}
            {!isMasterMode && !useRentalSidebar && (
                lockerItems
                    .filter((item) => item.permission)
                    .map((item) => (
                        <SidebarItem
                            className={'py-1'}
                            key={item.path}
                            to={item.path}
                            text={item.text}
                            icon={item.icon}
                            onClose={onClose}
                        />
                    ))
            )}

            {!isMasterMode && useRentalSidebar && (
                rentalItems
                    .filter((item) => item.permission)
                    .map((item) => (
                        <SidebarItem
                            className={'py-1'}
                            key={item.path}
                            to={item.path}
                            text={item.text}
                            icon={item.icon}
                            onClose={onClose}
                        />
                    ))
            )}

            {!isMasterMode &&
                commonItems
                    .filter((item) => item.permission)
                    .map((item) => (
                        <SidebarItem
                            className={'py-1'}
                            key={item.path}
                            to={item.path}
                            text={item.text}
                            icon={item.icon}
                            onClose={onClose}
                        />
                    ))}

            <div className={'mt-auto flex flex-col gap-1'}>
                {/* Every signed-in user, every role and mode (#2685). */}
                <SidebarItem
                    className={'py-1'}
                    to={paths.help}
                    text={t('side_bar.help')}
                    icon={<CircleHelp className={'h-5 w-5'} />}
                    onClose={onClose}
                />
                {canSeeAccount && (
                    <SidebarItem
                        className={'py-1'}
                        to={paths.account}
                        text={t('side_bar.account')}
                        icon={<Settings className={'h-5 w-5'} />}
                        onClose={onClose}
                    />
                )}
                <div className={'flex flex-row gap-2 px-3 py-2.5'}>
                    <button onClick={() => { i18n.changeLanguage('de'); persistLanguage('de'); }}>
                        <img
                            className={'object-cover size-5 rounded-full'}
                            src={images.flags.germany}
                            alt={'Germany'}
                        />
                    </button>
                    <button onClick={() => { i18n.changeLanguage('en'); persistLanguage('en'); }}>
                        <img
                            className={'object-cover size-5 rounded-full'}
                            src={images.flags.unitedKingdom}
                            alt={'United Kingdom'}
                        />
                    </button>
                </div>
                <div className={'lg:hidden'}>
                    <LogoutButton onLogout={signOut} />
                </div>
                {selfServiceTenant && (
                    <div className={'flex items-center justify-start gap-1.5 px-3 pt-2 pb-1 text-[11px] text-foreground/40'}>
                        <span>powered by</span>
                        <img
                            src={images.kolulaLogo}
                            alt={'Kolula'}
                            className={'h-3 opacity-60'}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
